import { useContext, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import { useSummaryTemplates } from "../../../hooks/useSummaryTemplates";
import type { SummaryTemplate } from "../../../types/summaryTemplate/SummaryTemplate";

const isSystemTemplate = (t: SummaryTemplate) => t.customerId === "system";
const isPersonalTemplate = (t: SummaryTemplate) =>
  t.customerId !== "system" && t.userId != null;
const isCustomerTemplate = (t: SummaryTemplate) =>
  t.customerId !== "system" && t.userId == null;

interface TemplateGroupProps {
  readonly label: string;
  readonly color: string;
  readonly templates: SummaryTemplate[];
  readonly selectedId: string | undefined;
  readonly onSelect: (id: string) => void;
}

function TemplateGroup({ label, color, templates, selectedId, onSelect }: TemplateGroupProps) {
  const { t } = useTranslation("summaryTemplate");
  if (templates.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box
        component="span"
        sx={{ fontSize: "0.6875rem", fontWeight: 600, color, letterSpacing: "0.02em" }}
      >
        {label}
      </Box>
      {templates.map((tpl) => {
        const isSelected = selectedId === tpl.id;
        const sectionNames = [...tpl.sections]
          .sort((a, b) => a.order - b.order)
          .map((s) => s.heading)
          .join(", ");

        return (
          <Box
            key={tpl.id}
            data-selected={isSelected}
            onClick={() => onSelect(tpl.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: "6px",
              cursor: "pointer",
              border: "1.5px solid",
              borderColor: isSelected ? color : "transparent",
              backgroundColor: isSelected ? `${color}10` : "transparent",
              "&:hover": {
                borderColor: color,
                backgroundColor: `${color}08`,
              },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: isSelected ? 600 : 400,
                    color: "var(--text-primary)",
                  }}
                >
                  {tpl.name}
                </Box>
                {tpl.isDefault && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.5625rem",
                      fontWeight: 700,
                      color: "#fff",
                      backgroundColor: color,
                      px: 0.5,
                      py: 0.1,
                      borderRadius: "3px",
                      lineHeight: 1.6,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {t("default")}
                  </Box>
                )}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: "0.6875rem",
                  color: "var(--text-tertiary)",
                  display: "block",
                  mt: 0.25,
                }}
              >
                {sectionNames || `${tpl.outputFormat} · ${tpl.lengthGuidance}`}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

interface InlineTemplatePickerProps {
  readonly customerId: string;
  readonly onSelect: (templateId: string) => void;
  readonly onCancel: () => void;
}

export default function InlineTemplatePicker({
  customerId,
  onSelect,
  onCancel,
}: InlineTemplatePickerProps) {
  const { t } = useTranslation(["summaryTemplate", "common"]);
  const { user } = useContext(AuthContext);
  const { data, isLoading } = useSummaryTemplates(customerId);

  const templates = data?.templates ?? [];
  const hiddenSystemTemplateIds = data?.hiddenSystemTemplateIds ?? [];

  const systemTemplates = templates.filter(
    (tpl) => isSystemTemplate(tpl) && !hiddenSystemTemplateIds.includes(tpl.id),
  );
  const customerTemplates = templates.filter(isCustomerTemplate);
  const personalTemplates = templates.filter(
    (tpl) => isPersonalTemplate(tpl) && tpl.userId === user?.userId,
  );

  const defaultTemplate = templates.find((tpl) => tpl.isDefault);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (defaultTemplate && !selectedId) {
      setSelectedId(defaultTemplate.id);
    }
  }, [defaultTemplate, selectedId]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Box
          component="span"
          sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}
        >
          {t("summaryTemplate:selectTemplateHint")}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        component="span"
        sx={{
          fontSize: "0.75rem",
          fontWeight: 600,
          color: "var(--text-secondary)",
          letterSpacing: "0.04em",
        }}
      >
        {t("summaryTemplate:selectTemplateHint")}
      </Box>

      <TemplateGroup
        label={t("summaryTemplate:systemTemplates")}
        color="#1976d2"
        templates={systemTemplates}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />
      <TemplateGroup
        label={t("summaryTemplate:customerTemplates")}
        color="#388e3c"
        templates={customerTemplates}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />
      <TemplateGroup
        label={t("summaryTemplate:myTemplates")}
        color="#f57c00"
        templates={personalTemplates}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
        <Box
          component="button"
          onClick={onCancel}
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: "var(--text-secondary)",
            background: "none",
            border: "1px solid var(--border-mid)",
            borderRadius: "6px",
            px: 1.5,
            py: 0.5,
            cursor: "pointer",
            fontFamily: "inherit",
            "&:hover": { borderColor: "var(--text-secondary)" },
          }}
        >
          {t("common:cancel")}
        </Box>
        <Box
          component="button"
          disabled={!selectedId}
          onClick={() => selectedId && onSelect(selectedId)}
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "#fff",
            background: "var(--archie-gradient)",
            border: "none",
            borderRadius: "6px",
            px: 1.5,
            py: 0.5,
            cursor: selectedId ? "pointer" : "default",
            fontFamily: "inherit",
            opacity: selectedId ? 1 : 0.5,
            "&:hover": selectedId ? { opacity: 0.88 } : {},
          }}
        >
          {t("summaryTemplate:generateSummary")}
        </Box>
      </Box>
    </Box>
  );
}
